import * as React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import styled from "styled-components"
import Card from "../components/Card/Card"
import ItemContainer from "../components/Item/ItemContainer"
import Metadata from "../components/Metadata/Metadata"
import HelpCenterController from "../controllers/HelpCenterController"
import { ArticleCollection, FeaturedArticle } from "../../types"
import PageLoader from "../components/Loader/PageLoader"
import { useSelector } from "react-redux"
import { AppState } from "../store"
import MobilityHelper from "../../helpers/MobilityHelper"
import { articleLink, collectionLink } from "../helpers/NavigationHelper"
import { Helmet } from "react-helmet"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
`

const FeaturedArticlesTitle = styled.div`
  color: #1a1a1a;
  line-height: 40px;
  font-weight: 700;
  font-size: 22px;
  padding-top: 8px;
  padding-bottom: 16px;
  padding-left: 12px;
  padding-right: 12px;
  margin-bottom: 4px;
`

const FeaturedArticles = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 24px;

  @media screen and (max-width: 767px) {
    display: flex;
    flex-direction: column;
    gap: 16px;
    grid-template-columns: none;
    grid-gap: 0;
  }
`

const CollectionItems = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-gap: 24px;

  @media screen and (max-width: 767px) {
    display: flex;
    flex-direction: column;
    grid-gap: 0;
    gap: 16px;
  }
`

const CollectionItem = styled(Link)`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 24px;
  background-color: white;
  border-radius: 8px;
  text-decoration: none;
  color: black;
  padding: 16px 24px;
  gap: 16px;
  border: 1px solid rgb(230, 230, 230);
  transition: all 0.15s linear;

  &:hover {
    border: 1px solid hsla(0, 0%, 0%, 0.6);
  }
`

const CollectionItemIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgb(244, 243, 237);
  width: 60px;
  height: 60px;
  min-width: 60px;
  min-height: 60px;
  border-radius: 8px;

  img {
    width: 28px;
    min-width: 28px;
    height: 28px;
    min-height: 28px;
    object-fit: cover;
  }
`

const CollectionItemContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const CollectionItemTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: black;
  margin-bottom: 2px;
  line-height: 24px;
  overflow: hidden;
  display: -webkit-box;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`

interface IState {
  featuredArticles: FeaturedArticle[]
  articleCollections: ArticleCollection[]
  didInitialLoad: boolean
}

const Overview = () => {
  const { currentLocale, helpCenter } = useSelector((state: AppState) => state.helpCenter)

  const { t } = useTranslation()
  const [state, setState] = React.useState<IState>({
    featuredArticles: [],
    articleCollections: [],
    didInitialLoad: false
  })

  const { featuredArticles, articleCollections, didInitialLoad } = state

  const fetchCollections = async () => {
    try {
      const { featured_articles, article_collections } = await HelpCenterController.getOverview(helpCenter.id)

      setState({
        didInitialLoad: true,
        featuredArticles: featured_articles,
        articleCollections: article_collections
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  React.useEffect(() => {
    fetchCollections().catch(console.error)
  }, [])

  if (!didInitialLoad) {
    return <PageLoader />
  }

  const title = `${helpCenter.name} - ${t('Overview::Help')}`
  const featuredArticlesTitle = MobilityHelper.getTranslation(currentLocale, helpCenter.translations, 'featured_articles_title')
  const localeFeaturedArticlesCount = featuredArticles.filter(featuredArticle => Boolean(MobilityHelper.getTranslation(currentLocale, featuredArticle.article.translations, 'title'))).length
  const localeArticleCollectionsCount = articleCollections.filter(collection => Boolean(MobilityHelper.getTranslation(currentLocale, collection.translations, 'title'))).length

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={title} />
      </Helmet>

      <Container>
        {localeFeaturedArticlesCount > 0 && <Card>
          {featuredArticlesTitle?.length > 0 && <FeaturedArticlesTitle>
            {featuredArticlesTitle}
          </FeaturedArticlesTitle>}
          <FeaturedArticles>
            {featuredArticles.map((featuredArticle) => {
              const title = MobilityHelper.getTranslation(currentLocale, featuredArticle.article.translations, 'title')

              if (!title) return null

              return (
                <ItemContainer key={featuredArticle.id} to={articleLink(featuredArticle.article)} title={title}>
                  {title}
                  <svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                </ItemContainer>
              )
            })}
          </FeaturedArticles>
        </Card>}

        {localeArticleCollectionsCount > 0 && <CollectionItems>
          {articleCollections.map((articleCollection) => {
            if (articleCollection.articles_count === 0) return null

            const title = MobilityHelper.getTranslation(currentLocale, articleCollection.translations, 'title')

            return (
              <CollectionItem key={articleCollection.id} to={collectionLink(articleCollection)}>
                <CollectionItemIcon>
                  <img src={articleCollection.icon} />
                </CollectionItemIcon>
                <CollectionItemContent>
                  <CollectionItemTitle>{title}</CollectionItemTitle>
                  <Metadata>
                    {helpCenter.show_article_authors && articleCollection.authors_count > 0 && <>
                      {t('Overview::{{count}} authors', { count: articleCollection.authors_count })}
                      <svg width="4" height="4" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="currentColor"  ><path d="M15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8Z"></path></svg>
                    </>}
                    {articleCollection.articles_count > 0 && <>
                      {t('Overview::{{count}} articles', { count: articleCollection.articles_count })}
                    </>}
                  </Metadata>
                </CollectionItemContent>
              </CollectionItem>
            )
          })}
        </CollectionItems>}
      </Container>
    </>
  )
}

export default Overview